/**
 * Centralized API Configuration
 *
 * Supports 4 environments: local | development | staging | production
 * Set REACT_APP_ENV to switch. Individual REACT_APP_*_URL vars override env defaults.
 *
 * Service Prefixes:
 * - Backend Service: /backend
 * - User Service: /user
 * - AI Service: /ai
 * - Integration Service: /integration
 */

import { getBaseURLs } from "./env.config";

// Base URLs from current environment (REACT_APP_ENV) with optional REACT_APP_* overrides
const urls = getBaseURLs();

/**
 * If REACT_APP_*_URL already ends with /backend, /user, etc., strip before we append
 * the service prefix again. Strip repeatedly so `.../backend/backend` also normalizes.
 */
function stripServiceSuffix(base, suffix) {
  if (!base || typeof base !== "string") return base;
  let trimmed = base.replace(/\/$/, "");
  while (trimmed.endsWith(suffix)) {
    trimmed = trimmed.slice(0, -suffix.length);
  }
  return trimmed;
}

// Base URLs (without service prefix)
export const API_BASE = {
  backend: urls.backend,
  user: urls.user,
  ai: urls.ai,
  integration: urls.integration,
};

// Prefixed URLs (new standard - preferred)
// stripServiceSuffix avoids double /backend, /integration, etc. when env URLs already include the suffix.
export const API_BASE_PREFIXED = {
  backend: `${stripServiceSuffix(API_BASE.backend, "/backend")}/backend`,
  user: `${stripServiceSuffix(API_BASE.user, "/user")}/user`,
  ai: `${stripServiceSuffix(API_BASE.ai, "/ai")}/ai`,
  integration: `${stripServiceSuffix(API_BASE.integration, "/integration")}/integration`,
};

// Legacy URLs (backward compatibility - fallback)
export const API_BASE_LEGACY = {
  backend: API_BASE.backend,
  user: API_BASE.user,
  ai: API_BASE.ai,
  integration: API_BASE.integration,
};

/**
 * Helper function to create an axios interceptor that retries with legacy endpoint on 404
 * This ensures zero downtime during rollout
 *
 * @param {Object} axiosInstance - Axios instance to add interceptor to
 * @param {string} prefixedBaseURL - Prefixed base URL
 * @param {string} legacyBaseURL - Legacy base URL
 */
export const addFallbackInterceptor = (
  axiosInstance,
  prefixedBaseURL,
  legacyBaseURL,
) => {
  // Request interceptor to use prefixed URL
  axiosInstance.interceptors.request.use(
    (config) => {
      // If URL starts with the prefixed base, keep it
      // If URL starts with the legacy base, replace with prefixed
      if (config.url && config.url.startsWith(legacyBaseURL)) {
        config.url = config.url.replace(legacyBaseURL, prefixedBaseURL);
      } else if (config.url && !config.url.startsWith("http")) {
        // Relative URL - prepend prefixed base
        config.baseURL = prefixedBaseURL;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  // Response interceptor to retry with legacy endpoint on 404
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // If error is 404 and we haven't retried yet, try legacy endpoint
      if (error.response?.status === 404 && !originalRequest._retry) {
        originalRequest._retry = true;

        // Replace prefixed URL with legacy URL
        if (
          originalRequest.url &&
          originalRequest.url.includes(prefixedBaseURL)
        ) {
          originalRequest.url = originalRequest.url.replace(
            prefixedBaseURL,
            legacyBaseURL,
          );
          originalRequest.baseURL = legacyBaseURL;

          // Retry the request with legacy endpoint
          try {
            return await axiosInstance(originalRequest);
          } catch (retryError) {
            return Promise.reject(retryError);
          }
        }
      }

      return Promise.reject(error);
    },
  );
};

const apiConfig = {
  API_BASE,
  API_BASE_PREFIXED,
  API_BASE_LEGACY,
  addFallbackInterceptor,
};

export default apiConfig;
